import type { RichTextData } from "@aprao/common/src/shapes/common.js";
import { css } from "@emotion/react";
import type { DeltaInsertOp } from "quill-delta-to-html";
import { QuillDeltaToHtmlConverter } from "quill-delta-to-html";
import {
  makeEndTag,
  makeStartTag,
} from "quill-delta-to-html/dist/commonjs/funcs-html";

const BrTag = "<br/>";

type Options = ConstructorParameters<typeof QuillDeltaToHtmlConverter>[1];

/**
 * The only purpose of this custom converter is to disabled new line collapsing
 * algorithm to stay consistent with Quill's behaviour.
 *
 * @see https://github.com/nozer/quill-delta-to-html/issues/80
 */
class KeepNewLineConverter extends QuillDeltaToHtmlConverter {
  constructor(deltaOps: Array<unknown>, options: Options) {
    super(deltaOps, options);
  }

  get superOptions() {
    // `options` is declared private in the parent class, so we centralise access
    // here to minimize the use of ts-ignore
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.options as unknown as NonNullable<Options>;
  }

  override _renderInlines(ops: Array<DeltaInsertOp>, isInlineGroup = true) {
    const html = ops
      .map((op: DeltaInsertOp) => {
        return this._renderInline(op, null) as unknown;
      })
      .join("");
    if (!isInlineGroup) {
      return html;
    }

    const startParaTag = makeStartTag(this.superOptions.paragraphTag);
    const endParaTag = makeEndTag(this.superOptions.paragraphTag);
    if (html === BrTag || this.superOptions.multiLineParagraph) {
      return startParaTag + html + endParaTag;
    }
    return (
      startParaTag +
      html
        .split(BrTag)
        .map((v) => {
          return v === "" ? BrTag : v;
        })
        .join(endParaTag + startParaTag) +
      endParaTag
    );
  }
}

export const RichTextRenderer = (props: { value: RichTextData }) => (
  <div
    css={css`
      overflow-wrap: break-word;

      h6,
      p,
      ul,
      ol {
        margin: 0;
      }

      ul,
      ol {
        list-style-position: inside;
        padding-left: 16px;
      }

      h6 {
        font-size: calc(1.1 * var(--report-root-font-size));
        font-weight: normal;
        // based on MUI gutter bottom value
        margin-bottom: 0.35em;
      }
    `}
    dangerouslySetInnerHTML={{
      __html: new KeepNewLineConverter(props.value.ops, {
        linkRel: "noopener",
      }).convert(),
    }}
  />
);
